/**
 * MediaAnalyseChip · iter145.1
 *
 * Wiederverwendbarer Chip für Upload-Points. Bietet:
 *   1) „Analysieren" – schickt File an /api/media/analyze und zeigt Score + Suggestions
 *   2) „Auto-Optimieren" – Preview via /optimize/preview, Confirm via /optimize/confirm
 *   3) „Gesicht verpixeln" – nur wenn showFaceBlur=true (STX)
 *
 * Props:
 *   - file: File | Blob                  (Pflicht)
 *   - onOptimizedFile: (File) => void    optional (bei Confirm)
 *   - showFaceBlur: boolean              default false
 *   - testIdSuffix: string               Präfix für data-testids
 *
 * Feature-Gates werden vom Backend (402/429) gesetzt – wir zeigen Upgrade-Hint.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Wand2, Eye, EyeOff, Loader2, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import api from "@/lib/api";

function scoreColor(v) {
  if (v >= 80) return "text-emerald-600";
  if (v >= 60) return "text-amber-600";
  return "text-red-600";
}

export default function MediaAnalyseChip({ file, onOptimizedFile, showFaceBlur = false, testIdSuffix = "" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [preview, setPreview] = useState(null); // { b64, filename }
  const [error, setError] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const tid = (s) => `mediachip-${testIdSuffix}-${s}`.replace(/-+/g, "-");

  const handleApiError = (e) => {
    const d = e?.response?.data?.detail;
    if (d?.code === "FEATURE_NOT_INCLUDED" || d?.code === "QUOTA_EXCEEDED") {
      setUpgrade({
        code: d.code,
        url: d.upgrade_hint?.url || "/studio/helper?tab=tariffs",
        cta: d.upgrade_hint?.cta || "Tarif ansehen und freischalten",
        currentPlan: d.current_plan,
      });
      return;
    }
    setError(d?.message || (typeof d === "string" ? d : "Aktion fehlgeschlagen"));
  };

  const analyze = async () => {
    if (!file) return;
    setBusy("analyze"); setError(null); setUpgrade(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data.analysis);
      setExpanded(true);
    } catch (e) {
      handleApiError(e);
    } finally { setBusy(null); }
  };

  const optimize = async () => {
    if (!file) return;
    setBusy("optimize"); setError(null); setUpgrade(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("auto", "true");
      const { data } = await api.post("/media/optimize/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview({ b64: data.preview_b64, filename: data.filename || (file.name || "optimized.jpg"), token: data.confirm_token });
      setExpanded(true);
    } catch (e) {
      handleApiError(e);
    } finally { setBusy(null); }
  };

  const confirmOptimize = async () => {
    if (!preview) return;
    setBusy("confirm"); setError(null);
    try {
      const { data } = await api.post("/media/optimize/confirm", { confirm_token: preview.token });
      // Backend liefert URL — wir laden das Bild als Blob und geben es als File zurück
      const url = data?.derivative_url || data?.url;
      if (url && onOptimizedFile) {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const optimizedFile = new File([blob], preview.filename, { type: blob.type });
        onOptimizedFile(optimizedFile);
      }
      setPreview(null);
      setAnalysis(null);
      setExpanded(false);
    } catch (e) {
      handleApiError(e);
    } finally { setBusy(null); }
  };

  const faceBlur = async () => {
    if (!file) return;
    setBusy("blur"); setError(null); setUpgrade(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/privacy/face-blur", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.blurred_url || data?.url;
      if (url && onOptimizedFile) {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const outFile = new File([blob], (file.name || "face-blur.jpg"), { type: blob.type });
        onOptimizedFile(outFile);
      }
    } catch (e) {
      handleApiError(e);
    } finally { setBusy(null); }
  };

  if (!file) return null;

  return (
    <div data-testid={tid("root")} className="mt-2 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={tid("analyze")}
          onClick={analyze}
          disabled={!!busy}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#0A0A0A] hover:bg-[#F5F5F5] disabled:opacity-40 flex items-center gap-1.5"
        >
          {busy === "analyze" ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          Analysieren
        </button>
        <button
          type="button"
          data-testid={tid("optimize")}
          onClick={optimize}
          disabled={!!busy}
          className="h-8 px-3 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#0A0A0A] hover:bg-[#F5F5F5] disabled:opacity-40 flex items-center gap-1.5"
        >
          {busy === "optimize" ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
          Auto-Optimieren
        </button>
        {showFaceBlur && (
          <button
            type="button"
            data-testid={tid("faceblur")}
            onClick={faceBlur}
            disabled={!!busy}
            className="h-8 px-3 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#0A0A0A] hover:bg-[#F5F5F5] disabled:opacity-40 flex items-center gap-1.5"
          >
            {busy === "blur" ? <Loader2 size={11} className="animate-spin" /> : <EyeOff size={11} />}
            Gesicht verpixeln
          </button>
        )}
      </div>

      {/* Upgrade-Hint */}
      {upgrade && (
        <div
          data-testid={tid("upgrade")}
          className="rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-2"
        >
          <Lock size={12} className="mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-bold">
              {upgrade.code === "QUOTA_EXCEEDED"
                ? "Monats-Kontingent aufgebraucht"
                : "Nicht in deinem Tarif enthalten"}
            </p>
            <p className="mt-0.5">{t("media.your_plan_prefix")} <b>{upgrade.currentPlan}</b>.</p>
            <button
              type="button"
              data-testid={tid("upgrade-cta")}
              onClick={() => navigate(upgrade.url)}
              className="mt-1 h-7 px-3 rounded-full bg-[#0A0A0A] text-white text-[10px] font-bold hover:bg-[#222]"
            >
              {upgrade.cta}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div data-testid={tid("error")} className="rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{String(error)}</span>
        </div>
      )}

      {/* Analyse-Ergebnis */}
      {analysis && expanded && (
        <div
          data-testid={tid("analysis")}
          className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-xs space-y-1.5"
        >
          <div className="flex items-baseline gap-3">
            {analysis.thumbnail_score != null && (
              <>
                <span className="text-[10px] uppercase tracking-wider text-[#666]">Score</span>
                <span className={`text-lg font-black ${scoreColor(analysis.thumbnail_score)}`}>
                  {Math.round(analysis.thumbnail_score)}
                </span>
                <span className="text-[10px] text-[#999]">/ 100</span>
              </>
            )}
          </div>
          <ul className="text-xs text-[#333] space-y-1">
            {(analysis.suggestions || []).map((s, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 size={11} className="text-emerald-600 mt-0.5 shrink-0" /> {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Optimize-Preview */}
      {preview && (
        <div
          data-testid={tid("preview")}
          className="rounded-xl border border-[#E5E5E5] bg-white p-3 text-xs space-y-2"
        >
          <p className="font-bold flex items-center gap-1.5">
            <Eye size={12} /> Vorschau der Optimierung
          </p>
          <img
            data-testid={tid("preview-img")}
            src={`data:image/jpeg;base64,${preview.b64}`}
            alt="Optimized preview"
            className="max-h-40 rounded-lg"
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={tid("confirm")}
              onClick={confirmOptimize}
              disabled={busy === "confirm"}
              className="h-8 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] disabled:opacity-40 flex items-center gap-1"
            >
              {busy === "confirm" ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              Übernehmen
            </button>
            <button
              type="button"
              data-testid={tid("discard")}
              onClick={() => setPreview(null)}
              className="h-8 px-3 rounded-full border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]"
            >
              Verwerfen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
